import type { CashierPerson } from './types'

/**
 * Выбор кассира «как в поиске»: строка поиска с подсказками. Без текста поиск предлагает подсказки — всегда не
 * меньше пяти, если в справочнике столько есть: сначала последние выбранные, остальное добирается первыми кассирами справочника
 * по алфавиту. С текстом — совпадения по имени. Выдача всегда короткая (никакого списка из всех кассиров), поэтому одинаково
 * работает при четырёх кассирах и при четырёхстах: лишнее уходит в «ещё N — уточните запрос».
 */

/** Сколько последних выбранных помнит поиск. */
export const RECENT_LIMIT = 5
/** Сколько подсказок поиск показывает без текста: последние выбранные и, если их меньше, первые по алфавиту. */
export const SUGGESTION_LIMIT = 5
/** Сколько совпадений показывает поиск с текстом. */
export const RESULTS_LIMIT = 8

export interface PickerOptions {
  /** `suggest` — текст поиска пуст, предложены подсказки; `search` — результаты поиска. */
  mode: 'suggest' | 'search'
  items: readonly CashierPerson[]
  /** Сколько первых подсказок — последние выбранные (остальные добраны из справочника); в режиме поиска всегда 0. */
  recentCount: number
  /** Сколько совпадений не поместилось в выдачу. */
  hidden: number
}

/** Имя для сравнения: без учёта регистра, «ё» как «е», лишние пробелы схлопнуты. */
export const normalizedName = (value: string): string => value.toLocaleLowerCase('ru-RU').replace(/ё/g, 'е').replace(/\s+/g, ' ').trim()

const byName = (left: CashierPerson, right: CashierPerson): number => left.name.localeCompare(right.name, 'ru')

/**
 * Что предложить в выпадающем списке. `selected` и `recent` — идентификаторы (регистр не важен); уже выбранные в выдачу не
 * попадают. Последние выбранные идут в порядке «свежий первым». С текстом совпадением считается имя, в котором встречается
 * каждое набранное слово (порядок слов не важен: «анна иванова» находит «Иванова Анна»); сначала те, чьё имя или слово
 * начинается с набранного, затем по алфавиту.
 */
export function pickerOptions(people: readonly CashierPerson[], selected: readonly string[], recent: readonly string[], query: string): PickerOptions {
  const taken = new Set(selected.map((id) => id.toLowerCase()))
  const available = people.filter((person) => !taken.has(person.id.toLowerCase()))
  const needle = normalizedName(query)
  if (needle === '') {
    const byId = new Map(available.map((person) => [person.id.toLowerCase(), person]))
    const recents = recent.flatMap((id) => {
      const person = byId.get(id.toLowerCase())
      return person === undefined ? [] : [person]
    })
    const latest = recents.slice(0, SUGGESTION_LIMIT)
    const used = new Set(latest.map((person) => person.id.toLowerCase()))
    const filler = [...available].filter((person) => !used.has(person.id.toLowerCase())).sort(byName).slice(0, SUGGESTION_LIMIT - latest.length)
    return { mode: 'suggest', items: [...latest, ...filler], recentCount: latest.length, hidden: 0 }
  }
  const words = needle.split(' ')
  const matches = available
    .map((person) => {
      const name = normalizedName(person.name)
      const nameWords = name.split(' ')
      const found = words.every((word) => name.includes(word))
      const prefixes = words.every((word) => nameWords.some((nameWord) => nameWord.startsWith(word)))
      return { person, found, prefixes }
    })
    .filter((entry) => entry.found)
    .sort((left, right) => Number(!left.prefixes) - Number(!right.prefixes) || byName(left.person, right.person))
    .map((entry) => entry.person)
  return { mode: 'search', items: matches.slice(0, RESULTS_LIMIT), recentCount: 0, hidden: Math.max(matches.length - RESULTS_LIMIT, 0) }
}

/** Куски имени для подсветки найденного: `matched` — часть, совпавшая с набранным словом. Пустой запрос — имя целиком. */
export function highlightParts(name: string, query: string): readonly { text: string; matched: boolean }[] {
  const words = normalizedName(query).split(' ').filter((word) => word !== '')
  const lowered = name.toLocaleLowerCase('ru-RU').replace(/ё/g, 'е')
  // Регистр и «ё» → «е» длину строки не меняют, позиции совпадают с исходным именем; если вдруг изменили — без подсветки.
  if (lowered.length !== name.length) return [{ text: name, matched: false }]
  const marked = Array.from({ length: name.length }, () => false)
  for (const word of words) {
    for (let at = lowered.indexOf(word); at >= 0; at = lowered.indexOf(word, at + word.length)) for (let index = at; index < at + word.length; index += 1) marked[index] = true
  }
  const parts: { text: string; matched: boolean }[] = []
  for (let index = 0; index < name.length; index += 1) {
    const matched = marked[index] === true
    const last = parts[parts.length - 1]
    if (last !== undefined && last.matched === matched) last.text += name.charAt(index)
    else parts.push({ text: name.charAt(index), matched })
  }
  return parts
}

/** «ещё 3 — уточните запрос»; `null`, когда всё поместилось. */
export const hiddenText = (hidden: number): string | null => (hidden > 0 ? `ещё ${hidden} — уточните запрос` : null)

/** Последние выбранные после ещё одного выбора: он первый, повтор не дублируется, больше `RECENT_LIMIT` не хранится. */
export function rememberedAfter(recent: readonly string[], cashierId: string): string[] {
  return [cashierId, ...recent.filter((id) => id.toLowerCase() !== cashierId.toLowerCase())].slice(0, RECENT_LIMIT)
}

/** Кассир в окне добавления в состав: `recent` — его добавляли недавно. */
export interface MemberCandidate {
  person: CashierPerson
  recent: boolean
}

/**
 * Весь список для окна «Добавить сотрудника»: все кассиры справочника, кроме тех, кто уже в составе (`taken`, регистр не важен).
 * Без текста — недавно добавленные первыми (свежий первым), дальше остальные по алфавиту; с текстом — совпадения по имени в любом
 * порядке слов: сначала те, чьё слово начинается с набранного, затем недавние, затем по алфавиту. Выдача не обрезается: окно
 * прокручивается, поэтому при четырёхстах кассирах список целый, а поиск лишь сужает его.
 */
export function memberCandidates(people: readonly CashierPerson[], taken: readonly string[], recent: readonly string[], query: string): MemberCandidate[] {
  const closed = new Set(taken.map((id) => id.toLowerCase()))
  const recentOrder = new Map(recent.map((id, index) => [id.toLowerCase(), index]))
  const available = people.filter((person) => !closed.has(person.id.toLowerCase()))
  const rank = (person: CashierPerson): number => recentOrder.get(person.id.toLowerCase()) ?? recent.length
  const needle = normalizedName(query)
  const words = needle === '' ? [] : needle.split(' ')
  return available
    .map((person) => {
      const name = normalizedName(person.name)
      const nameWords = name.split(' ')
      return {
        person,
        found: words.every((word) => name.includes(word)),
        prefixes: words.every((word) => nameWords.some((nameWord) => nameWord.startsWith(word))),
      }
    })
    .filter((entry) => entry.found)
    .sort((left, right) => Number(!left.prefixes) - Number(!right.prefixes) || rank(left.person) - rank(right.person) || byName(left.person, right.person))
    .map((entry) => ({ person: entry.person, recent: rank(entry.person) < recent.length }))
}
